import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import OlMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';

import { CanvasToolbarIconComponent } from '../../../icons/canvas-toolbar-icon.component';
import {
  isCanvasImportFileName,
  layerNameFromImportPath,
  parseCanvasGeoJsonText,
  parseCanvasImportZip,
  type CanvasImportedLayer,
} from '../../canvas-geojson-import.utils';

export interface ImportedFeaturesPayload {
  layers: CanvasImportedLayer[];
  mode: 'merge' | 'replace';
}

@Component({
  selector: 'lib-canvas-import-tool',
  standalone: true,
  imports: [CanvasToolbarIconComponent],
  template: `
    <span class="cv-tb-import-wrap">
      @if (importHint(); as hint) {
        <div class="cv-tb-import-hint" role="alert">{{ hint }}</div>
      }

      @if (showMergeDialog()) {
        <div class="cv-import-dialog-backdrop" (click)="cancelImport()">
          <div class="cv-import-dialog" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">

            <!-- Leaf decoration (MahaAgX pattern) -->
            <div class="cv-import-dialog__leaf">
              <img src="/assets-leaf.png" alt="" aria-hidden="true" />
            </div>

            <!-- Icon -->
            <div class="cv-import-dialog__icon">
              <svg viewBox="0 0 24 24" fill="none" style="stroke: var(--canvas-action)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="12" y1="3" x2="12" y2="21"/>
                <polyline points="9 8 7 12 9 16"/>
                <polyline points="15 8 17 12 15 16"/>
              </svg>
            </div>

            <h2 class="cv-import-dialog__title">Import layers</h2>
            <p class="cv-import-dialog__body">
              The map already has layers.<br/>What would you like to do?
            </p>

            <div class="cv-import-dialog__actions">
              <button
                type="button"
                class="cv-import-dialog__btn cv-import-dialog__btn--cancel"
                (click)="cancelImport()"
              >Cancel</button>
              <button
                type="button"
                class="cv-import-dialog__btn cv-import-dialog__btn--merge"
                (click)="confirmImport('merge')"
                title="Add imported shapes alongside existing layers"
              >Merge</button>
              <button
                type="button"
                class="cv-import-dialog__btn cv-import-dialog__btn--replace"
                (click)="confirmImport('replace')"
                title="Remove existing layers and load only the imported shapes"
              >Replace</button>
            </div>
          </div>
        </div>
      }

      <input
        #fileInput
        type="file"
        accept=".zip,.json,.geojson,application/zip,application/geo+json,application/json"
        class="cv-tb-import__input"
        (change)="onFileSelected($event)"
      />
      <button
        class="cv-tb__btn"
        (click)="pickFile()"
        title="Import a GeoJSON file or a zip of GeoJSON layers (from Export → GeoJSON)."
        type="button"
      >
        <lib-canvas-toolbar-icon name="import"></lib-canvas-toolbar-icon>
      </button>
    </span>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class ImportToolComponent implements OnDestroy {
  @Input({ required: true }) map!: OlMap;
  @Input({ required: true }) userDrawSource!: VectorSource;
  /** True when the layers panel already has rows (drawings or datasets). */
  @Input() hasExistingLayers = false;

  @Output() featuresImported = new EventEmitter<ImportedFeaturesPayload>();

  @ViewChild('fileInput') private fileInput!: ElementRef<HTMLInputElement>;

  readonly importHint = signal<string | null>(null);
  readonly showMergeDialog = signal(false);

  private pendingLayers: CanvasImportedLayer[] = [];
  private importHintTimer: ReturnType<typeof setTimeout> | null = null;

  pickFile(): void {
    this.fileInput.nativeElement.click();
  }

  ngOnDestroy(): void {
    if (this.importHintTimer) {
      clearTimeout(this.importHintTimer);
      this.importHintTimer = null;
    }
  }

  onFileSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (!isCanvasImportFileName(file.name)) {
      this.showImportHint('Only .zip, .geojson, or .json files can be imported.');
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'zip') {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const bytes = new Uint8Array(reader.result as ArrayBuffer);
          const layers = parseCanvasImportZip(bytes, this.featureProjection());
          this.handleParsedLayers(layers);
        } catch (e) {
          console.error('Import failed', e);
          this.showImportHint('Could not read that zip as Canvas GeoJSON layers.');
        }
      };
      reader.onerror = () => this.onReadError();
      reader.readAsArrayBuffer(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const layer = parseCanvasGeoJsonText(
          reader.result as string,
          this.featureProjection(),
          layerNameFromImportPath(file.name),
        );
        this.handleParsedLayers(layer ? [layer] : []);
      } catch (e) {
        console.error('Import failed', e);
        this.showImportHint('Could not read that file as GeoJSON.');
      }
    };
    reader.onerror = () => this.onReadError();
    reader.readAsText(file);
  }

  confirmImport(mode: 'merge' | 'replace'): void {
    const layers = this.pendingLayers;
    this.pendingLayers = [];
    this.showMergeDialog.set(false);
    this.applyImport(layers, mode);
  }

  cancelImport(): void {
    this.pendingLayers = [];
    this.showMergeDialog.set(false);
  }

  private handleParsedLayers(layers: CanvasImportedLayer[]): void {
    if (!layers.length) {
      this.showImportHint('No features found in that file.');
      return;
    }

    if (this.hasExistingLayers || this.userDrawSource.getFeatures().length > 0) {
      this.pendingLayers = layers;
      this.showMergeDialog.set(true);
      return;
    }
    this.applyImport(layers, 'merge');
  }

  private applyImport(
    layers: CanvasImportedLayer[],
    mode: 'merge' | 'replace',
  ): void {
    this.featuresImported.emit({ layers, mode });
  }

  private featureProjection(): string {
    return this.map.getView().getProjection()?.getCode() ?? 'EPSG:4326';
  }

  private onReadError(): void {
    console.error('Unable to read file');
    this.showImportHint('Could not read the file.');
  }

  private showImportHint(text: string): void {
    if (this.importHintTimer) {
      clearTimeout(this.importHintTimer);
      this.importHintTimer = null;
    }
    this.importHint.set(text);
    this.importHintTimer = setTimeout(() => {
      this.importHint.set(null);
      this.importHintTimer = null;
    }, 5500);
  }
}
